/**
 * TOSS Web Application — Client portal: one answer to «можно ли предложить установку»
 *
 * Three places ask it and must never disagree: the card that floats over the content
 * (`ClientInstallPrompt`), the row in the sidebar's account menu (`ClientAccountBlock`) and the row
 * in the phone's profile hub (`ClientProfileInstallSection`).
 *
 * The split between the loud one and the quiet two is the owner's decision of 25.08.2026: the card
 * appears once a week at most and goes silent for seven days when it is closed, while the rows stay
 * for as long as the browser will still install anything. Closing the card hides the interruption,
 * not the feature — so **dismissal is deliberately not read here**. It belongs to the card alone.
 *
 * Lives in its own file rather than beside the components it serves so that `ClientPwaBanners.tsx`
 * exports components and nothing else (Fast Refresh).
 */

import { useCallback } from 'react';
import { usePWA } from '@/hooks/usePWA';

// ============================================================================
// Dismissal
// ============================================================================

/**
 * The portal's OWN key. Sharing the internal one would mean that dismissing the card in the office
 * app also hides it in the cabinet — two products, one memory.
 */
const STORAGE_KEY = 'client-pwa-install-dismissed-at';
const DISMISS_DAYS = 7;

/** Written once the cabinet is actually installed: never offer it again on this device. */
export const PERMANENT_DISMISS = 'installed';

export function isInstallDismissed(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (!value) return false;
    if (value === PERMANENT_DISMISS) return true;

    const dismissedAt = Number.parseInt(value, 10);
    if (Number.isNaN(dismissedAt)) return false;

    return (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24) < DISMISS_DAYS;
  } catch {
    // Private mode: show it. A card shown once too often is better than a broken screen.
    return false;
  }
}

export function rememberInstallDismissal(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Nothing to do — the dismissal simply does not survive this session.
  }
}

/** iOS has no `beforeinstallprompt`: there the offer explains the two taps instead of opening one. */
function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
  return isIOS && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
}

// ============================================================================
// Hook
// ============================================================================

/** The sentence iOS gets instead of a dialog. */
const IOS_HINT = 'Нажмите «Поделиться», затем «На экран „Домой“»';

export interface ClientInstallOffer {
  /** The browser can install the cabinet and it is not installed yet. */
  isAvailable: boolean;
  /** iOS Safari: no `beforeinstallprompt`, so the offer can only explain the two taps. */
  isIOS: boolean;
  /** Opens the browser's own dialog. Resolves `false` on iOS and on a refusal. */
  install: () => Promise<boolean>;
  /** The sentence iOS gets instead of a dialog. */
  iosHint: string;
}

export function useClientInstallOffer(): ClientInstallOffer {
  const { canInstall, promptInstall, isStandalone } = usePWA();
  const isIOS = isIOSSafari();

  const install = useCallback(async () => {
    const accepted = await promptInstall();
    if (accepted) rememberInstallDismissal(PERMANENT_DISMISS);
    return accepted;
  }, [promptInstall]);

  return {
    isAvailable: !isStandalone && (canInstall || isIOS),
    isIOS,
    install,
    iosHint: IOS_HINT,
  };
}
